import React, { useState } from "react";
import { HelpCircle } from "lucide-react";

export function ImportFormatsHelp() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="muted-text hover:text-amber-400 p-1"
        title="What CSV / PDF layouts does the importer support?"
        data-testid="import-formats-help-button"
      >
        <HelpCircle size={14} />
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={() => setOpen(false)}
          data-testid="import-formats-help-modal"
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] overflow-y-auto"
            style={{ background: "#11061d", border: "1px solid #3f3f3f" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #1f1f1f" }}>
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">Import help</div>
                <div className="text-base font-semibold tracking-tight">Supported file layouts</div>
              </div>
              <button onClick={() => setOpen(false)} className="muted-text hover:text-white">✕</button>
            </div>

            <div className="px-6 py-4 space-y-6 text-sm">
              <section>
                <div className="text-amber-400 font-medium mb-1">CSV — flat, one row per entry</div>
                <div className="muted-text text-xs mb-2">
                  Column order doesn&rsquo;t matter — we match by header name (case &amp; punctuation
                  insensitive). Recognised header synonyms:
                </div>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li><b>Photographer code</b>: <span className="mono">Code · Member ID · Member No · Membership Number · Photographer Code · No · Number · ID</span></li>
                  <li><b>Photographer name</b>: <span className="mono">Name · Member Name · Full Name · Photographer · Photographer Name</span></li>
                  <li><b>Image title</b>: <span className="mono">Title · Image Title · Image Name · Photo Title · Photo Name · Image</span></li>
                  <li><b>Score</b>: <span className="mono">Score · Points · Mark · Marks</span></li>
                  <li><b>Placement</b> <span className="muted-text">(optional)</span>: <span className="mono">Placement</span> with values <span className="mono">FIRST / SECOND / THIRD / HIGHLY_COMMENDED / COMMENDED / NONE</span></li>
                </ul>
                <pre
                  className="mt-2 p-3 text-xs mono overflow-x-auto"
                  style={{ background: "#0a0a0a", border: "1px solid #1f1f1f" }}
                >
{`Code,Name,Image Title,Score
AB,Alice Brown,Sunset,15
BM,Bill Metson,River,17`}
                </pre>
                <pre
                  className="mt-2 p-3 text-xs mono overflow-x-auto"
                  style={{ background: "#0a0a0a", border: "1px solid #1f1f1f" }}
                >
{`Order,Image Name,Member ID,Score,Name
11,Old Timer,103,9.5,Mick Sadler
18,Say Nothing,103,10,Mick Sadler`}
                </pre>
              </section>

              <section>
                <div className="text-amber-400 font-medium mb-1">RTF — Word / OpenOffice / DiCentra export</div>
                <div className="muted-text text-xs mb-2">
                  Save your document from Word / Google Docs / OpenOffice as{" "}
                  <span className="mono">Rich Text Format (.rtf)</span>, or upload the{" "}
                  <span className="mono">.rtf</span> that DiCentra emits alongside the results PDF.
                  The importer auto-detects the layout:
                </div>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li><b>DiCentra / PhotoCompetitionPro exports</b> — same &ldquo;Entrant Scores&rdquo; and &ldquo;Images in Ranked Order&rdquo; layouts that the PDF importer understands, including the top awards summary for 1st / 2nd / 3rd placements.</li>
                  <li><b>Word / Google Docs table exports</b> — one row per entry with columns following the CSV synonyms above.</li>
                  <li><b>Plain CSV pasted into a document</b> — commas + newlines are preserved through the RTF strip.</li>
                </ul>
              </section>

              <section>
                <div className="text-amber-400 font-medium mb-1">PDF — &ldquo;Entrant Scores&rdquo; layout (PhotoCompetitionPro)</div>
                <div className="muted-text text-xs mb-2">
                  One block per photographer. Half-mark glyph <span className="mono">½</span> is converted to <span className="mono">.5</span>.
                </div>
                <pre
                  className="p-3 text-xs mono overflow-x-auto"
                  style={{ background: "#0a0a0a", border: "1px solid #1f1f1f" }}
                >
{`Entrant Scores
Avtar Verdee Total: 7½
2 Chromatic Ascension 7½
Bill Metson Total: 17
19 Bishop Waynflete's Chantry Chapel 9
26 Slogger Rose's Bed and Breakfast 8`}
                </pre>
              </section>

              <section>
                <div className="text-amber-400 font-medium mb-1">PDF — &ldquo;Images in Ranked Order&rdquo; layout</div>
                <div className="muted-text text-xs mb-2">
                  Two lines per entry. Inline <span className="mono">First / Second / Third</span>{" "}
                  becomes the placement. Points-based scores above 20 are accepted.
                </div>
                <pre
                  className="p-3 text-xs mono overflow-x-auto"
                  style={{ background: "#0a0a0a", border: "1px solid #1f1f1f" }}
                >
{`Images in Ranked Order
4 Claimed by the Sea 31 First
Danny Pearce
14 Victoria Coastal View 15 Second
Paul Adams
19 'Indian Summer' 10
Simon Smith`}
                </pre>
              </section>

              <section>
                <div className="text-amber-400 font-medium mb-1">Photographer matching</div>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Match is <b>case-insensitive</b> and ignores punctuation — <span className="mono">BILL METSON</span> = <span className="mono">Bill Metson</span>.</li>
                  <li>If a name doesn&rsquo;t exactly match, the &ldquo;Resolve unmatched names&rdquo; dialog suggests close matches (top pick auto-selected).</li>
                  <li>Pick &ldquo;Leave unmatched&rdquo; for first-time entrants — the row still imports and shows the typed name on results so you can fix it later.</li>
                </ul>
              </section>

              <section>
                <div className="text-amber-400 font-medium mb-1">Re-importing</div>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Default: uploads are <b>idempotent</b> — rows already present (matched on photographer + image title) are skipped, only NEW rows are added.</li>
                  <li>Tick <span className="mono">Re-import to update</span> to overwrite scores / placements on rows that already exist (useful for fixing a typo&rsquo;d score after the fact).</li>
                </ul>
              </section>
            </div>

            <div className="px-6 py-4 flex justify-end" style={{ borderTop: "1px solid #1f1f1f" }}>
              <button
                onClick={() => setOpen(false)}
                className="ccs-btn-primary text-sm"
                data-testid="import-formats-help-close"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
